import { Trip, TripParticipant } from '../types';

/**
 * Serviço para geração e manipulação de QR codes
 */
export class QRCodeService {
  /**
   * Gera URL do QR code para um participante
   */
  static generateQRCodeData(participant: TripParticipant, trip: Trip): object {
    return {
      voucher_code: participant.voucher_code,
      participant_name: participant.name,
      participant_document: participant.document_number,
      hotel_name: trip.hotel_name,
      check_in_date: trip.start_date,
      check_out_date: trip.end_date,
      individual_amount: participant.individual_amount,
      is_exempt: participant.is_exempt,
      generated_at: new Date().toISOString(),
      trip_id: trip.id
    };
  }

  /**
   * Download de um único QR code como PNG
   */
  static downloadQRCode(dataURL: string, voucherCode: string, participantName: string) {
    const link = document.createElement('a');
    link.download = `qr-code-${voucherCode}-${participantName.replace(/\s+/g, '-')}.png`;
    link.href = dataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Compartilhar QR code via Web Share API ou fallback
   */
  static async shareQRCode(dataURL: string, voucherCode: string, participantName: string, hotelName: string) {
    if (!navigator.share) {
      // Fallback: copiar código do voucher
      try {
        await navigator.clipboard.writeText(voucherCode);
        return { success: true, method: 'clipboard', message: 'Código do voucher copiado!' };
      } catch (err) {
        return { success: false, message: 'Erro ao copiar código' };
      }
    }

    try {
      // Converter data URL para blob
      const response = await fetch(dataURL);
      const blob = await response.blob();
      const file = new File([blob], `qr-code-${voucherCode}.png`, { type: 'image/png' });

      await navigator.share({
        title: `QR Code - ${participantName}`,
        text: `QR Code para entrada em ${hotelName}`,
        files: [file]
      });

      return { success: true, method: 'share' };
    } catch (err) {
      // Fallback para clipboard
      try {
        await navigator.clipboard.writeText(voucherCode);
        return { success: true, method: 'clipboard', message: 'Código do voucher copiado!' };
      } catch (clipboardErr) {
        return { success: false, message: 'Erro ao compartilhar' };
      }
    }
  }
}

/**
 * Serviço para geração de PDFs com múltiplos QR codes
 */
export class PDFService {
  /**
   * Gera PDF com todos os QR codes de uma viagem
   * Nota: Esta é uma implementação placeholder. Em produção, você pode usar
   * bibliotecas como jsPDF, react-pdf, ou um serviço backend
   */
  static async generateTripQRCodesPDF(trip: Trip): Promise<void> {
    // TODO: Implementar geração real de PDF
    console.log('Generating PDF for trip:', trip.id);
    
    // Por enquanto, vamos simular o download
    const pdfContent = this.generatePDFContent(trip);
    const blob = new Blob([pdfContent], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `qr-codes-${trip.hotel_name.replace(/\s+/g, '-')}-${trip.start_date}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }

  /**
   * Gera conteúdo do PDF (placeholder)
   */
  private static generatePDFContent(trip: Trip): string {
    const participants = trip.participants.filter(p => p.voucher_code);
    
    return `
# QR Codes - ${trip.hotel_name}

**Período:** ${trip.start_date} a ${trip.end_date}
**Total de Participantes:** ${participants.length}

## Participantes e Vouchers:

${participants.map(p => `
- **${p.name}**
  - Voucher: ${p.voucher_code}
  - Documento: ${p.document_number}
  - Valor: R$ ${(p.individual_amount || 0) / 100}
  - Status: ${p.is_exempt ? 'Isento' : 'Pago'}
`).join('\n')}

---
Gerado em: ${new Date().toLocaleString('pt-BR')}
    `.trim();
  }
}

/**
 * Serviço para manipulação de arquivos
 */
export class FileService {
  /**
   * Download de arquivo genérico
   */
  static downloadFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Copia texto para a área de transferência
   */
  static async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }

  /**
   * Verifica se o navegador suporta Web Share API
   */
  static canShare(): boolean {
    return 'share' in navigator;
  }

  /**
   * Verifica se o navegador suporta Clipboard API
   */
  static canCopyToClipboard(): boolean {
    return 'clipboard' in navigator;
  }
}

export { QRCodeService as default };
